/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, Fragment, useEffect } from 'react';
import generator from '../utils/generator';
import classNames from 'classnames';
import { useApi } from '../contexts/ApiHandler';
import Markdown from './Markdown';
import AsyncapiBindings from './AsyncapiBindings';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { eventPath } from 'utils/navigation';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionItem,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TabList,
  TabPanels,
  TabPanel
} from '@carbon/react';

const AsyncApiOperationOverview = ({ channelName }) => {
  // init hooks
  const { apiHandler, api } = useApi();
  const { t } = useTranslation();

  // state
  const [id] = useState(() => uniqueId('channel-overview-'));
  const [parameters, updateParameterValues] = useState([]);

  // vars
  const channelData = apiHandler.getChannel(channelName);
  const markupType = apiHandler.getMarkupType();
  const operations = apiHandler.getChannel(channelName)?.operations().all();
  const description = apiHandler.getDescription(channelName);
  const channelBindings = apiHandler.getChannelBindings(channelName);

  useEffect(() => {
    let parameters = apiHandler.getParameters(channelName);
    parameters.forEach(async (param, index) => {
      await generator
        .generateDummyValue({
          propertyName: 'content',
          property: param.schema || {},
          empty: false
        })
        .then(
          (data) => {
            parameters[index].value = data;
            updateParameterValues(parameters);
          },
          (error) => {
            console.error(error);
          }
        );
    });
  }, [apiHandler, channelName]);

  const renderParameterSchema = () => {
    const parameterType = 'path';
    if (parameters.length === 0) return null;

    return (
      <>
        <div className="operationInformation operationSummary operationParms channel__parameter_body">
          <div role="heading" aria-level={3} className="channel__labelText">
            {t('parameters')}
          </div>
          <div className="channelParameters">
            <Accordion align="start">
              <AccordionItem className={classNames(`parameter-${parameterType}`)} key={parameterType} title={t(parameterType)} open={true}>
                <Table>
                  <TableBody>
                    {parameters.map((param, index) => {
                      let exampleContent = param.schema && param.schema.type ? param.value : null;
                      return (
                        <Fragment key={`fragment_${index}`}>
                          <TableRow>
                            <TableCell className="channel__parameterNameContainer">
                              <div className="channel__parameterName" data-testid={'channel-parameter-name'}>
                                {param.name}
                              </div>
                              <div className="requiredHighlight parameterIn">{t('required')}</div>
                            </TableCell>
                            <TableCell className="channel__parameterDescriptionContainer">
                              {param.description && (
                                <Markdown
                                  className="parameterDescription"
                                  data={param.description}
                                  markupType="markdown"
                                  data-testid={'channel-description-name'}
                                />
                              )}
                              {param.schema && param.schema.type && (
                                <>
                                  <div className="parameterType schema">
                                    <div data-testid={'channel-schema-type'}>{param.schema.type}</div>
                                  </div>
                                  <div className="channel__tabsExample">
                                    <Tabs className={'channel__tab'}>
                                      <TabList aria-label="channel tab">
                                        <Tab title={t('example')} data-testid={'channel-example-tab'}>
                                          {t('example')}
                                        </Tab>
                                      </TabList>
                                      <TabPanels>
                                        <TabPanel>
                                          <div className="cds--snippet--multi channel__tabExample" data-testid={'channel-example-snippet'}>
                                            {exampleContent}
                                          </div>
                                        </TabPanel>
                                      </TabPanels>
                                    </Tabs>
                                  </div>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </>
    );
  };

  const channelDescription = (description) => {
    return <Markdown data-testid="channel-overview-description" data={description} markupType={markupType} />;
  };

  const renderOperation = (operationLabel, { summary, description }) => {
    const labelId = `${id}-${operationLabel}-operation`.replace(/ /g, '');
    return (
      (summary || description) && (
        <div className="channel__overviewInformation" data-testid={'channel-overview-summary'}>
          <div role="heading" aria-level={3} className="channel__label" id={labelId}>
            {operationLabel}
          </div>
          <div className="channel__value" role="region" data-testid={'channel-overview-summary-value'} aria-labelledby={labelId}>
            {summary ? <p>{summary}</p> : null}
            {description ? <Markdown data={description} markupType={markupType} /> : null}
          </div>
        </div>
      )
    );
  };

  const tableOfContents = () => {
    return (
      <div data-testid="channel-tableofcontents" className="channel__value">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>{t('operation')}</TableHeader>
              <TableHeader>{t('description')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {operations.map((operation) => {
              const channelType = operation.action();
              const dataRoute = eventPath(api, channelName, 'operation/' + (operation.id() ? encodeURIComponent(operation.id()) : channelType));
              return (
                <TableRow>
                  <TableCell>
                    <Link to={dataRoute}>
                      <p data-testid={`channel-${channelType}-link`}>{t(`channel_${channelType}_operation`, { channelName: operation.id() })}</p>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <p>{t(`channel_${channelType}_description`)}</p>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };
  let renderTable = true;
  return (
    <section className={classNames('channel', 'main')}>
      <div className="channel__header" role="heading" aria-level={2} aria-label={t('channel_overview_label')} data-testid={'channel-overview-header'}>
        {channelName}
      </div>
      {channelBindings && <AsyncapiBindings bindings={channelBindings} />}
      <div className="channel__body divided">
        {!channelData || Object.values(channelData).length === 0 ? (
          <div className="channel__overviewInformation" data-testid={'channel-error'} aria-label={t('channel_overview_error_label')}>
            {t('channel_overview_error')}
          </div>
        ) : (
          <>
            {description ? channelDescription(description) : null}
            {renderParameterSchema()}
            {operations.map((operation) => {
              const operationDesc = operation.description();
              const operationSummary = operation.summary();
              if (operationDesc || operationSummary) {
                renderTable = false;
              }
              return (
                <>
                  {operationDesc || operationSummary
                    ? renderOperation(t(`channel_overview_${operation.action()}`, operation.id()), { summary: operationSummary, description: operationDesc })
                    : null}
                </>
              );
            })}
            {renderTable && operations && operations.length > 0 && tableOfContents()}
          </>
        )}
      </div>
    </section>
  );
};

export default AsyncApiOperationOverview;
